import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/side-nav.default.js';
import * as wireframeSkin from './skins/side-nav.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

class RunSideNav extends RunElement {
  #collapsed = false;
  #disabled = false;
  #label = 'Site navigation';
  #rendering = false;

  // Cached references to slotted child primitives
  #navSections = [];
  #links = [];

  static get observedAttributes() {
    return ['collapsed', 'disabled', 'label'];
  }

  // Arrow-function closures — private methods are not writable;
  // never use this.#method = this.#method.bind(this).
  _onKeydownBound              = (e) => this.#handleKeydown(e);
  _onSlotChangeBound           = (e) => this.#handleSlotChange(e);
  _onNavSectionToggleBound     = (e) => this.#handleNavSectionToggle(e);
  _onLinkActivateBound         = (e) => this.#handleLinkActivate(e);

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'navigation');
    this.addEventListener('keydown', this._onKeydownBound);
    // Shadow-root listeners for delegated child events — exploit composed
    // bubbling through the shadow boundary.
    this.shadowRoot.addEventListener('run-nav-section-toggle', this._onNavSectionToggleBound);
    this.shadowRoot.addEventListener('run-link-activate', this._onLinkActivateBound);
    // Named-slot slotchange listeners. The skin renders slots into the shadow
    // DOM during the first updated() cycle; any pre-existing slots found here
    // (on reconnect) are attached immediately. Post-first-render slots are
    // (re-)attached inside updated() after each skin render.
    this.shadowRoot.querySelectorAll('slot[name]').forEach(slot => {
      slot.addEventListener('slotchange', this._onSlotChangeBound);
    });
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this._onKeydownBound);
    this.shadowRoot.removeEventListener('run-nav-section-toggle', this._onNavSectionToggleBound);
    this.shadowRoot.removeEventListener('run-link-activate', this._onLinkActivateBound);
    this.shadowRoot.querySelectorAll('slot[name]').forEach(slot => {
      slot.removeEventListener('slotchange', this._onSlotChangeBound);
    });
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'collapsed': this.collapsed = newValue !== null; break;
      case 'disabled':  this.disabled  = newValue !== null; break;
      case 'label':     this.label     = newValue ?? ''; break;
    }
  }

  get collapsed() { return this.#collapsed; }
  set collapsed(value) {
    const coerced = Boolean(value);
    if (this.#collapsed === coerced) return;
    this.#collapsed = coerced;
    this.toggleAttribute('collapsed', coerced);
    this.#updateAria();
    this.requestUpdate();
    this.#propagateToChildren();
  }

  get disabled() { return this.#disabled; }
  set disabled(value) {
    const coerced = Boolean(value);
    if (this.#disabled === coerced) return;
    this.#disabled = coerced;
    this.toggleAttribute('disabled', coerced);
    this.#updateAria();
    this.requestUpdate();
    this.#propagateToChildren();
  }

  get label() { return this.#label; }
  set label(value) {
    const coerced = String(value ?? '').trim() || 'Site navigation';
    if (this.#label === coerced) return;
    this.#label = coerced;
    this.setAttribute('label', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  toggle() {
    if (this.#disabled) return;
    this.collapsed = !this.#collapsed;
    this.#emitToggle();
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
      // Re-attach slotchange listeners after skin has (re-)rendered the shadow
      // DOM so that any newly created slot elements are covered.
      this.shadowRoot.querySelectorAll('slot[name]').forEach(slot => {
        slot.removeEventListener('slotchange', this._onSlotChangeBound);
        slot.addEventListener('slotchange', this._onSlotChangeBound);
      });
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #updateAria() {
    this.setAttribute('aria-label', this.#label);
    if (this.#disabled) {
      this.setAttribute('aria-disabled', 'true');
    } else {
      this.removeAttribute('aria-disabled');
    }
    // collapsed drives visual state via attribute; the toggle control rendered
    // by the skin owns aria-expanded. The nav landmark itself does not carry
    // aria-expanded per the ARIA Authoring Practices Guide (landmarks are not
    // disclosure widgets).
  }

  #handleKeydown(event) {
    if (this.#disabled) return;
    if (event.key === 'Escape' && !this.#collapsed) {
      event.preventDefault();
      this.toggle();
    }
  }

  #handleSlotChange(event) {
    const slot = event.target;
    const slotName = slot.name;
    const assigned = slot.assignedElements({ flatten: true });

    // Propagate current disabled state to newly slotted children.
    // name is never propagated — prevents double form submission.
    assigned.forEach(el => {
      if ('disabled' in el) el.disabled = this.#disabled;
    });

    // Refresh cached child references from the default (unnamed) slot, which
    // receives run-nav-section and run-link elements placed by the consumer.
    if (!slotName) {
      this.#navSections = assigned.filter(el => el.localName === 'run-nav-section');
      this.#links       = assigned.filter(el => el.localName === 'run-link');
    }

    // Toggle optional-region presence attributes so skin CSS can react.
    this.toggleAttribute(`data-has-${slotName || 'default'}`, assigned.length > 0);
  }

  #handleNavSectionToggle(event) {
    event.stopPropagation();
    this.dispatchEvent(new CustomEvent('run-side-nav-section-toggle', {
      bubbles: true,
      composed: true,
      detail: {
        ...event.detail,
        source: event.target.localName,
      },
    }));
  }

  #handleLinkActivate(event) {
    event.stopPropagation();
    this.dispatchEvent(new CustomEvent('run-side-nav-link-activate', {
      bubbles: true,
      composed: true,
      detail: {
        ...event.detail,
        source: event.target.localName,
      },
    }));
  }

  #propagateToChildren() {
    [...this.#navSections, ...this.#links].forEach(el => {
      if ('disabled' in el) el.disabled = this.#disabled;
    });
  }

  #emitToggle() {
    this.dispatchEvent(new CustomEvent('run-side-nav-toggle', {
      bubbles: true,
      composed: true,
      detail: { collapsed: this.#collapsed },
    }));
  }
}

if (!customElements.get('run-side-nav')) {
  customElements.define('run-side-nav', RunSideNav);
}

export { RunSideNav };
export default RunSideNav;